import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  IconDeviceSim,
  IconPlus,
  IconAlertCircle,
  IconCheck,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { useAgentRegisterSIM, type RegisterSIMRequest } from "@/hooks/use-agent-sims";

import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/sims/register/")({
  component: SIMRegisterPage,
});

function SIMRegisterPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <SIMRegisterContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function SIMRegisterContent() {
  const [formData, setFormData] = useState<RegisterSIMRequest>({
    phone_number: "",
    operator: "bkash",
    pin: "",
    account_type: "personal",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const registerSIM = useAgentRegisterSIM();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.phone_number || !formData.pin) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.phone_number.length !== 11 || !formData.phone_number.startsWith("01")) {
      toast.error("Please enter a valid 11-digit phone number starting with 01");
      return;
    }

    if (formData.pin.length < 4 || formData.pin.length > 6) {
      toast.error("PIN must be between 4 and 6 digits");
      return;
    }

    setIsSubmitting(true);
    try {
      await registerSIM.mutateAsync(formData);
      toast.success("SIM registration submitted successfully! It's now pending admin approval.");
      // Reset form
      setFormData({
        phone_number: "",
        operator: "bkash",
        pin: "",
        account_type: "personal",
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to register SIM");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof RegisterSIMRequest) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    let value = e.target.value;

    // Only allow numbers for phone and PIN
    if (field === "phone_number" || field === "pin") {
      value = value.replace(/\D/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mx-auto">
            <IconDeviceSim className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold">Register New SIM</h1>
          <p className="text-muted-foreground">
            Register a new SIM card for MFS transactions. Your registration will be reviewed and approved by an admin.
          </p>
        </div>

        {/* Info Alert */}
        <Alert>
          <IconAlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Note:</strong> All SIM registrations require admin approval before they can be used for transactions.
            Make sure all information is accurate to avoid rejection.
          </AlertDescription>
        </Alert>

        {/* Registration Form */}
        <Card>
          <CardHeader>
            <CardTitle>SIM Information</CardTitle>
            <CardDescription>
              Enter the details of the SIM card you want to register
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Phone Number */}
              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone Number *</Label>
                <Input
                  id="phone_number"
                  placeholder="01712345678"
                  value={formData.phone_number}
                  onChange={handleInputChange("phone_number")}
                  maxLength={11}
                  className="font-mono"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Enter the 11-digit phone number starting with 01
                </p>
              </div>

              {/* Operator */}
              <div className="space-y-2">
                <Label htmlFor="operator">Mobile Operator *</Label>
                <Select
                  value={formData.operator}
                  onValueChange={(value: "bkash" | "nagad" | "rocket") =>
                    setFormData((prev) => ({ ...prev, operator: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select operator" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bkash">bKash</SelectItem>
                    <SelectItem value="nagad">Nagad</SelectItem>
                    <SelectItem value="rocket">Rocket</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Select the mobile financial service operator
                </p>
              </div>

              {/* PIN */}
              <div className="space-y-2">
                <Label htmlFor="pin">SIM PIN *</Label>
                <Input
                  id="pin"
                  type="password"
                  placeholder="Enter 4-6 digit PIN"
                  value={formData.pin}
                  onChange={handleInputChange("pin")}
                  maxLength={6}
                  pattern="[0-9]{4,6}"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Enter the SIM PIN (4-6 digits). This will be encrypted and stored securely.
                </p>
              </div>

              {/* Account Type */}
              <div className="space-y-2">
                <Label htmlFor="account_type">Account Type</Label>
                <Select
                  value={formData.account_type}
                  onValueChange={(value: "personal" | "agent") =>
                    setFormData((prev) => ({ ...prev, account_type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Select the type of MFS account
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || registerSIM.isPending}
              >
                {isSubmitting || registerSIM.isPending ? (
                  "Submitting..."
                ) : (
                  <>
                    <IconPlus className="w-4 h-4 mr-2" />
                    Register SIM
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Registration Process Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IconCheck className="w-5 h-5 text-green-600" />
              Registration Process
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
                  1
                </div>
                <div>
                  <p className="font-medium">Submit Registration</p>
                  <p className="text-sm text-muted-foreground">
                    Fill out and submit the registration form
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
                  2
                </div>
                <div>
                  <p className="font-medium">Admin Review</p>
                  <p className="text-sm text-muted-foreground">
                    Admin will review and verify your SIM details
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
                  3
                </div>
                <div>
                  <p className="font-medium">Approval</p>
                  <p className="text-sm text-muted-foreground">
                    Once approved, you can use this SIM for transactions
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}